// vim: set ts=2 sts=2 sw=2 et:
//
// Mapeos WRPF <-> OpenLifter para el payload de resultados. Espejo de
// packages/api/src/openlifter/mappings.ts y csv-import.ts del repo web (no se importan para no
// acoplar los repos): mantener en sync.

import type { Entry } from "../../types/dataTypes";
import type { WrpfEntryAnchor } from "./types";
import { WRPF_WEIGHT_MEN, WRPF_WEIGHT_WOMEN } from "./wrpfArgentinaRules";
import { equipmentForEvent } from "../../logic/equipmentForEvent";

/** Par (evento, división) WRPF en el que compite una entry. Valores = enums del backend WRPF. */
export type WrpfModalityPair = {
  event: string;
  division: string;
  /** Código de evento OpenLifter ("SBD", "SB", "B", ...) para calcular total/puntos/puesto. */
  eventCode: string;
};

/** Equipamiento WRPF implícito en un enum de evento WRPF (SBD_CLASSIC -> CLASSIC). */
export function equipmentForWrpfEvent(event: string): string {
  if (event.includes("CLASSIC")) return "CLASSIC";
  if (event.includes("SINGLE_PLY")) return "SINGLE_PLY";
  if (event.includes("MULTI_PLY")) return "MULTI_PLY";
  return "RAW";
}

/** Equipamiento WRPF desde el string de equipamiento de una entry OpenLifter. */
export function equipmentForOpenlifterEntry(equipment: string): string {
  const v = equipment.toLowerCase();
  if (v.includes("multi")) return "MULTI_PLY";
  if (v.includes("single")) return "SINGLE_PLY";
  if (v.includes("wrap")) return "CLASSIC";
  return "RAW";
}

/** Equipamiento WRPF efectivo para una modalidad concreta de la entry. */
export function equipmentForOpenlifterEvent(
  entry: Entry,
  eventCode: Entry["events"][number],
  wrapsOnlyAffectSquat: boolean,
): string {
  return equipmentForOpenlifterEntry(equipmentForEvent(entry.equipment, eventCode, wrapsOnlyAffectSquat));
}

/** Código de evento OpenLifter ("SBD", "B", ...) desde un enum de evento WRPF ("SBD_RAW", "B_RAW"). */
export function openlifterEventCode(wrpfEvent: string): string {
  if (wrpfEvent.startsWith("SBD")) return "SBD";
  if (wrpfEvent.startsWith("BD")) return "BD";
  if (wrpfEvent.startsWith("S")) return "S";
  if (wrpfEvent.startsWith("B")) return "B";
  if (wrpfEvent.startsWith("D")) return "D";
  return "SBD";
}

/** Enum de evento WRPF desde código OpenLifter + equipamiento WRPF (réplica de mapEvent web). */
export function wrpfEventFromCode(eventCode: string, equipment: string): string {
  const v = (eventCode || "SBD").toUpperCase().trim();

  if (v.startsWith("SBD")) {
    const suffix =
      equipment === "CLASSIC"
        ? "CLASSIC"
        : equipment === "SINGLE_PLY"
          ? "SINGLE_PLY"
          : equipment === "MULTI_PLY"
            ? "MULTI_PLY"
            : "RAW";
    return `SBD_${suffix}`;
  }
  if (v === "BD") {
    if (equipment === "SINGLE_PLY") return "BD_SINGLE_PLY";
    if (equipment === "MULTI_PLY") return "BD_MULTI_PLY";
    return "BD";
  }
  if (v === "S") {
    if (equipment === "CLASSIC") return "S_CLASSIC";
    if (equipment === "SINGLE_PLY") return "S_SINGLE_PLY";
    if (equipment === "MULTI_PLY") return "S_MULTI_PLY";
    return "S";
  }
  if (v === "B") {
    if (equipment === "SINGLE_PLY") return "B_SINGLE_PLY";
    if (equipment === "MULTI_PLY") return "B_MULTI_PLY";
    return "B";
  }
  if (v === "D") {
    if (equipment === "SINGLE_PLY") return "D_SINGLE_PLY";
    if (equipment === "MULTI_PLY") return "D_MULTI_PLY";
    return "D";
  }
  return "SBD_RAW";
}

/**
 * Enum de división WRPF desde el string de división OpenLifter (réplica de mapDivision web).
 * Tolera los strings legacy de bandas de 4-5 años ("M40-44", "M45-49" -> MASTER1).
 */
export function wrpfDivisionFromString(value: string | undefined | null): string {
  const v = (value ?? "").trim().toLowerCase();
  if (!v) return "OPEN";

  if (v.includes("j14") || v.includes("14-16") || v.includes("teen")) return "TEEN";
  if (v.includes("j17") || v.includes("17-19") || v.includes("subjunior")) return "SUBJUNIOR";
  if (v.includes("j20") || v.includes("20-23") || (v.includes("junior") && !v.includes("sub"))) {
    return "JUNIOR";
  }
  if (v.includes("open")) return "OPEN";

  if (v.includes("m80") || v.includes("master 5") || v === "m5") return "MASTER5";
  if (v.includes("m70") || v.includes("m75") || v.includes("master 4") || v === "m4") return "MASTER4";
  if (v.includes("m60") || v.includes("m65") || v.includes("master 3") || v === "m3") return "MASTER3";
  if (v.includes("m50") || v.includes("m55") || v.includes("master 2") || v === "m2") return "MASTER2";
  if (v.includes("m40") || v.includes("m45") || v.includes("master 1") || v === "m1") return "MASTER1";
  if (v.includes("master") || v.startsWith("m")) return "MASTER1";

  return "OPEN";
}

/**
 * Modalidades WRPF (evento, división) en las que compite una entry. Un atleta inscripto en
 * J20-23 + Open produce UNA fila de resultado POR división al publicar a la web (antes se publicaba
 * sólo la primaria y se perdía la premiación Open).
 *
 * Fuentes, en orden (dedup por (evento, división)):
 * 1. Anchor (modalidades inscriptas en la web): filtradas al equipamiento de ESTA entry (los
 *    bundles viejos estampan la lista completa en todos los anchors) y validadas contra el estado
 *    VIVO de la entry — si el director quitó la división/evento en Registration, no se publica.
 * 2. Unión con eventos × divisiones de la propia entry: walk-ins y altas hechas el día del torneo,
 *    igual que lo que muestra la pestaña Results.
 */
export function modalityPairsForEntry(
  entry: Entry,
  anchor: WrpfEntryAnchor | undefined,
  wrapsOnlyAffectSquat: boolean,
): WrpfModalityPair[] {
  const pairs: WrpfModalityPair[] = [];
  const push = (pair: WrpfModalityPair) => {
    if (!pairs.some((p) => p.event === pair.event && p.division === pair.division)) {
      pairs.push(pair);
    }
  };

  if (anchor) {
    const liveDivisions = new Set(entry.divisions.map((d) => wrpfDivisionFromString(d)));
    const candidates = [
      { event: anchor.primaryEvent, division: anchor.primaryDivision },
      ...(anchor.secondaryModalities ?? []),
    ];
    for (const c of candidates) {
      const eventCode = openlifterEventCode(c.event);
      const equipment = equipmentForOpenlifterEvent(entry, eventCode as Entry["events"][number], wrapsOnlyAffectSquat);
      if (equipmentForWrpfEvent(c.event) !== equipment) continue;
      if (entry.events.length > 0 && !entry.events.includes(eventCode as Entry["events"][number])) continue;
      if (entry.divisions.length > 0 && !liveDivisions.has(c.division)) continue;
      push({ event: c.event, division: c.division, eventCode });
    }
  }

  const events: ReadonlyArray<string> = entry.events.length > 0 ? entry.events : ["SBD"];
  const divisions: ReadonlyArray<string> = entry.divisions.length > 0 ? entry.divisions : [""];
  for (const ev of events) {
    for (const div of divisions) {
      const equipment = equipmentForOpenlifterEvent(entry, ev as Entry["events"][number], wrapsOnlyAffectSquat);
      push({
        event: wrpfEventFromCode(ev, equipment),
        division: wrpfDivisionFromString(div),
        eventCode: ev,
      });
    }
  }

  return pairs;
}

/**
 * Categoría de peso WRPF (enum del backend, p.ej. "M_CAT82") desde el peso corporal del PESAJE.
 * Espejo de weightClassFromBodyweightKg del repo web (67.5 -> CAT67, 82.5 -> CAT82).
 */
export function wrpfWeightClassFromBodyweight(
  bodyweightKg: number | null | undefined,
  sex: Entry["sex"],
): string | null {
  if (bodyweightKg == null || !(bodyweightKg > 0)) return null;
  const isWoman = sex === "F";
  const buckets = isWoman ? WRPF_WEIGHT_WOMEN : WRPF_WEIGHT_MEN;
  const prefix = isWoman ? "F" : "M";
  for (const maxKg of buckets) {
    if (bodyweightKg <= maxKg) return `${prefix}_CAT${Math.floor(maxKg)}`;
  }
  return `${prefix}_CATHW`;
}

/** Categoría de peso WRPF desde el label de inscripción de OpenLifter ("82.5", "+140"). */
export function wrpfWeightClassFromLabel(label: string, sex: Entry["sex"]): string | null {
  const v = (label ?? "").trim();
  if (!v) return null;
  if (v.startsWith("+")) return sex === "F" ? "F_CATHW" : "M_CATHW";
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? wrpfWeightClassFromBodyweight(n, sex) : null;
}

/**
 * Categoría en la que compite una entry: la del PESAJE (autoritativo en powerlifting); si no pesó,
 * la inscripta (anchor) o la intención de la entry. Misma regla que el rebuild de resultados de la
 * web: los récords se evalúan en la misma categoría en la que se publica el resultado.
 */
export function wrpfCompetitionWeightClass(
  entry: Pick<Entry, "bodyweightKg" | "sex" | "intendedWeightClassKg">,
  anchor: Pick<WrpfEntryAnchor, "weightClass"> | undefined,
): string | null {
  return (
    wrpfWeightClassFromBodyweight(entry.bodyweightKg, entry.sex) ??
    anchor?.weightClass ??
    wrpfWeightClassFromLabel(entry.intendedWeightClassKg, entry.sex)
  );
}

/** Un peso corporal dentro de la categoría WRPF dada: su límite, o 5 kg sobre el último en la abierta. */
export function wrpfBodyweightForWeightClass(weightClass: string): number | null {
  const prefix = weightClass.slice(0, 2);
  if (prefix !== "F_" && prefix !== "M_") return null;
  const buckets: ReadonlyArray<number> = prefix === "F_" ? WRPF_WEIGHT_WOMEN : WRPF_WEIGHT_MEN;
  if (weightClass.endsWith("_CATHW")) return buckets[buckets.length - 1] + 5;
  return buckets.find((maxKg) => weightClass === `${prefix}CAT${Math.floor(maxKg)}`) ?? null;
}
